import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, notification } from "antd";
import Icon from "@ant-design/icons";
import { httpService } from "../../../../base/services/httpService.service";
import { FetchStatusEnum, StatusCodesEnum } from "../../../../utils/enums";
import https from "../../../../base/utils/http";
import TabContainer from "../../../../components/DeletedItemsComponents/TabContainer/TabContainer";
import DataTable from "../../../../components/DeletedItemsComponents/DataTable/DataTable";
import { DeleteComponent } from "../../workspace/components/DeleteComponent";
import { notifyPlanLimitRefusal } from "../../../../components/Billing/planLimits";
import { describeArchiveFailure } from "../../../../components/Archive/archiveApi";

const PAGE_SIZE = 10;

function WorkspacesTab() {
   const [data, setData] = useState([]);
   const [status, setStatus] = useState(FetchStatusEnum.Idle);
   const [totalSize, setTotalSize] = useState(0);
   const [searchValue, setSearchValue] = useState("");
   const [deleteModal, setDeleteModal] = useState("");

   const [searchParams, setSearchParams] = useSearchParams();

   const entityType = searchParams.get("entityType");
   const page = searchParams.get("page") || 1;

   const handleRestore = async (entity) => {
      try {
         const params = new URLSearchParams({
            entityType: entityType,
            isDeleted: false
         });
         const res = await https.put(
            `/DataCollection/${entity.id}/toggleDeleteOrRestore?${params.toString()}`
         );

         notification.open({
            type: res.data.statusCode === StatusCodesEnum.OK ? "success" : "error",
            message: res.data.message || "Something went wrong! Please try again later."
         });
         fetchData();
      } catch (error) {
         // Refused, the workspace stays in Deleted items: over the plan's
         // limit (409), or any other refusal, both in the server's words, as
         // unarchiving says them.
         if (notifyPlanLimitRefusal(error)) return;
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeArchiveFailure(
               error,
               "We had a problem restoring the workspace, please try again."
            )
         });
      }
   };

   const handleDelete = async (entity) => {
      setStatus(FetchStatusEnum.Loading);
      try {
         const res = await https.post(`/Workspaces/delete`, { id: entity.id });
         notification.open({
            type: res.data.statusCode === StatusCodesEnum.OK ? "success" : "error",
            message: res.data.message || "Something went wrong! Please try again later."
         });
         fetchData();
      } catch (error) {
         setStatus(FetchStatusEnum.Idle);
         if (
            error.response.status === 400 &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.includes &&
            error.response.data.errors.includes(
               "One or more of the projects in this workspace have surveys and can't be deleted!"
            )
         ) {
            notification.error({
               message:
                  "One or more of the projects in this workspace have surveys and can't be deleted!"
            });
         } else {
            notification.error({
               message: "Error deleting workspace, please try again later."
            });
         }
      }
   };

   const handleDeleteAll = async () => {
      try {
         setStatus(FetchStatusEnum.Loading);
         const params = new URLSearchParams({
            isDeleted: true
         });
         const res = await https.post(
            `/Workspaces/permanentlyDeleteOrRestoreAllWorkspacesOfUser?${params.toString()}`
         );

         const isSuccess = res.data.statusCode === StatusCodesEnum.OK;

         if (isSuccess) {
            notification.success({
               type: "success",
               message: "Entities have been deleted successfully!"
            });
            fetchData();
         } else {
            notification.warning({
               type: "warning",
               message: res.data.message || "Something went wrong! Please try again later."
            });
            setStatus(FetchStatusEnum.Idle);
         }
      } catch (_err) {
         notification.error({
            message: "Error deleting all workspaces, please try again later."
         });
         setStatus(FetchStatusEnum.Idle);
      }
   };

   const handleRestoreAll = async () => {
      try {
         setStatus(FetchStatusEnum.Loading);

         const params = new URLSearchParams({
            isDeleted: true,
            restoreAll: true
         });
         const res = await https.post(
            `/Workspaces/permanentlyDeleteOrRestoreAllWorkspacesOfUser?${params.toString()}`
         );
         const isSuccess = res.data.statusCode === StatusCodesEnum.OK;
         if (isSuccess) {
            notification.success({
               type: "success",
               message: "Entities restored successfully!"
            });
            fetchData();
         } else {
            notification.warning({
               type: "warning",
               message: res.data.message || "Something went wrong! Please try again later."
            });
            setStatus(FetchStatusEnum.Idle);
         }
      } catch (err) {
         if (!notifyPlanLimitRefusal(err)) {
            notification.error({
               message:
                  err.response?.data?.message || "Error restoring workspaces, please try again later."
            });
         }
         setStatus(FetchStatusEnum.Idle);
      }
   };

   const fetchData = useCallback(async () => {
      setStatus(FetchStatusEnum.Loading);

      const params = new URLSearchParams({
         isDeleted: "true",
         search: searchValue,
         page: page,
         pageSize: PAGE_SIZE
      });

      httpService.get(
         `/Workspaces/getMyWorkspaces?${params.toString()}`,
         (res) => {
            setStatus(FetchStatusEnum.Success);
            setData(res.data);
            setTotalSize(res.data?.totalSize || res.data?.length || 0);

            // TODO: This endpoint doesn't have pagination
            // if (res.data?.length === 0 && res.data?.totalSize !== 0) {
            //    setSearchParams({ entityType, page: page > 1 ? page - 1 : 1 });
            // }
         },
         (_err) => {
            setStatus(FetchStatusEnum.Error);
         }
      );
   }, [page, searchValue]);

   useEffect(() => {
      if (!entityType) {
         return;
      }
      fetchData();
   }, [entityType, fetchData]);

   const handleOk = () => {
      setDeleteModal("open");
   };

   const columns = [
      {
         title: "Name",
         dataIndex: "name",
         key: "name"
      },
      {
         title: "Description",
         dataIndex: "description",
         key: "description"
      }
   ];

   return (
      <TabContainer
         searchValue={searchValue}
         setSearchValue={setSearchValue}
         title={"Deleted Workspaces"}
         searchPlaceholder={"Search by name"}
      >
         <DataTable
            dataSource={data}
            columns={columns}
            status={status}
            refetch={fetchData}
            entityType={entityType}
            handleRestore={handleRestore}
            handleDelete={handleDelete}
            pagination={{
               total: totalSize,
               pageSize: PAGE_SIZE,
               current: page,
               onChange: (page) => setSearchParams({ entityType, page })
            }}
         />
         <div className="deleted-items-modal-footer deleted-items-footer-buttons">
            <Button
               key="restore"
               onClick={handleRestoreAll}
               disabled={data?.length === 0 || status === FetchStatusEnum.Loading}
               loading={status === FetchStatusEnum.Loading}
               className="cancel-button"
            >
               Restore all
            </Button>
            <Button
               key="delete"
               onClick={handleOk}
               disabled={data?.length === 0 || status === FetchStatusEnum.Loading}
               loading={status === FetchStatusEnum.Loading}
               className="ok-button"
               icon={
                  <Icon
                     component={() => (
                        <svg
                           width="15"
                           height="16"
                           viewBox="0 0 15 16"
                           fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                        >
                           <path
                              d="M0.542267 4.00133H14.2566M4.54227 4.00133V3.4299C4.54227 2.67214 4.84328 1.94541 5.3791 1.40959C5.91492 0.873773 6.64165 0.572754 7.39941 0.572754C8.15717 0.572754 8.8839 0.873773 9.41972 1.40959C9.95554 1.94541 10.2566 2.67214 10.2566 3.4299V4.00133M5.68512 7.43157V12.0048M9.1137 7.43157V12.0048M2.25655 4.00133H12.5423V14.287C12.5423 14.5901 12.4218 14.8809 12.2075 15.0952C11.9932 15.3094 11.7025 15.4299 11.3994 15.4299H3.39941C3.0963 15.4299 2.80562 15.3094 2.59128 15.0952C2.37696 14.8809 2.25655 14.5901 2.25655 14.287V4.00133Z"
                              stroke="white"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                           />
                        </svg>
                     )}
                  />
               }
            >
               Delete all
            </Button>
         </div>
         {deleteModal !== "" && (
            <DeleteComponent
               open={deleteModal.length > 0}
               setOpen={() => setDeleteModal("")}
               handleDelete={handleDeleteAll}
               id={deleteModal}
            />
         )}
      </TabContainer>
   );
}
export default WorkspacesTab;
